import { useUserContext } from "@repo/baseCloud";
import { PushProcessingNotification } from "@repo/helpers";
import { Button, ButtonTypes, Loading } from "@repo/ui";
import { FC } from "react";

import { useAssociationsApi, useAssociationsQueries } from "../../api/associationsApi";
import { useLocationQueries } from "../../api/locationApi";
import {
	AssociationsInfoProvider,
	useAssociationsInfoContext,
	useLocationContext,
} from "../../contexts/editableItemContext";
import { Filters } from "./components/filters";
import { Marketers } from "./components/marketers";
import style from "./index.module.scss";

export interface AssociationsProps {
	readOnly?: boolean;
}

export const Associations: FC = () => {
	const { locationId } = useLocationContext();
	const { user } = useUserContext();
	const { useAssociationsQuery } = useAssociationsQueries(locationId);
	const { data: associations, loading } = useAssociationsQuery();

	const { userIsAdmin, userIsVendorUser } = user;
	const readOnly = !userIsAdmin && !userIsVendorUser;

	return (
		<Loading loading={loading} message="Loading Associations..." fullHeight>
			<AssociationsInfoProvider item={{ associations, originalAssociations: associations }}>
				<AssociationsInternal readOnly={readOnly} />
			</AssociationsInfoProvider>
		</Loading>
	);
};

export const AssociationsInternal: FC<AssociationsProps> = ({ readOnly }) => {
	const { locationId } = useLocationContext();
	const { associations, originalAssociations, pendingChanges, saving, setSaving, resetAssociations } =
		useAssociationsInfoContext();

	const { saveAssociations } = useAssociationsApi();
	const { useAssociationsQuery } = useAssociationsQueries(locationId);
	const { useStatusHistoriesQuery, useLocationInfoQuery } = useLocationQueries();
	const { clearQuery: clearLocationQuery } = useLocationInfoQuery(locationId);
	const { clearQuery: clearAssociationsQuery } = useAssociationsQuery();
	const { clearQuery: clearStatusHistoriesQuery } = useStatusHistoriesQuery(locationId);

	async function save() {
		const notify = PushProcessingNotification("Saving Associations...");
		try {
			setSaving(true);
			const changedAssociations = associations.reduce(function (filtered, marketer) {
				const originalMarketer = originalAssociations.find((m) => m.marketerId == marketer.marketerId);
				const marketerUpdated = originalMarketer.marketerEnabled != marketer.marketerEnabled;
				const branches = marketer.branches.filter((branch) => {
					const originalBranch = originalMarketer.branches.find((b) => b.branchId == branch.branchId);
					return originalBranch.branchEnabled != branch.branchEnabled;
				});
				if (marketerUpdated || branches.length) filtered.push({ ...marketer, branches });
				return filtered;
			}, []);

			await saveAssociations(+locationId, changedAssociations);
			await clearLocationQuery();
			await clearAssociationsQuery();
			await clearStatusHistoriesQuery();
			notify.success("Associations saved!");
		} catch (ex) {
			notify.error("Failed to save associations");
			throw ex;
		} finally {
			setSaving(false);
		}
	}

	return (
		<div className={style.container}>
			<Filters />
			<h3>Vendors</h3>
			<Marketers readOnly={readOnly} />
			{!readOnly && (
				<div className={style.actions}>
					<Button
						types={[ButtonTypes.Primary]}
						size="large"
						disabled={!pendingChanges || saving}
						onClick={save}
					>
						Save
					</Button>
					<Button size="large" hidden={!pendingChanges} disabled={saving} onClick={resetAssociations}>
						Cancel
					</Button>
				</div>
			)}
		</div>
	);
};
